// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// tanstack
import {
  ColumnDef,
  ColumnResizeMode,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

// mui
import { alpha, darken, useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// components
import { Icon, IconType } from '@/components/atoms/Icon/Icon';

// types
import { Icons } from '@/components/atoms/Icon/icons';

// hooks
import { useWindowSize } from '@/hooks/useWindowSize';

// theme
import theme from '@/theme';

/**
 * DataTableColumn mirrors the subset of MUI GridColDef that consumers use,
 * so existing column definitions continue to work unchanged.
 */
export interface DataTableColumn {
  /** Row data key */
  field: string;
  /** Column header label */
  headerName?: string;
  /** CSS flex value for column width */
  flex?: number;
  /** Minimum width in px */
  minWidth?: number;
  /** Fixed width in px */
  width?: number;
  /** Whether the column is sortable (default true) */
  sortable?: boolean;
  /** Whether the column is filterable (default true) */
  filterable?: boolean;
  /** Hide column menu */
  disableColumnMenu?: boolean;
  /** Custom cell renderer */
  renderCell?: (params: { row: any; value: any }) => React.ReactNode;
  /** Compute a display value from the row */
  valueGetter?: (value: any, row: any) => any;
}

// Re-export for consumers — GridColDef is an alias for DataTableColumn
export type GridColDef = DataTableColumn;
export type GridRenderCellParams = { row: any; value: any };

/**
 * DataTableAction defines an action button for each row.
 */
export interface DataTableAction {
  /** Tooltip text */
  label: string;
  /** Icon name, as listed in the Icon atom registry */
  icon: Icons;
  /** Icon style variant */
  iconType?: IconType;
  /** Hide action for specific rows */
  isHidden?: (row: any) => boolean;
  /** Disable action for specific rows */
  isDisabled?: (row: any) => boolean;
  /** Action handler */
  onPress: (row: any) => void;
}

/**
 * DataTableProps defines the props for the DataTable component.
 */
export interface DataTableProps {
  /** Column definitions */
  columns: DataTableColumn[];
  /** Row data — each row must have an `id` field */
  rows: any[];
  /** Row action icons */
  actions?: DataTableAction[];
  /** Default action when a row is clicked */
  defaultAction?: DataTableAction;
  /** Toolbar buttons (e.g., "New Organization") */
  toolbarActions?: React.ReactNode;
  /** Message shown when there are no rows */
  noDataMsg?: string;
  /** Show search bar */
  isSearchable?: boolean;
  /** Enable pagination */
  isPaginated?: boolean;
  /** Show total row count */
  isTotalRows?: boolean;
  /** Number of rows per page */
  rowsPerPage?: number;
  /** Pagination mode — 'client' (default) or 'server' */
  paginationMode?: 'client' | 'server';
  /** Total row count for server-side pagination */
  rowCount?: number;
  /** Current page (0-indexed) for controlled server-side pagination */
  page?: number;
  /** Callback when pagination changes (for server-side pagination) */
  onPaginationChange?: (page: number, pageSize: number) => void;
  /** Whether data is loading (shows loading state) */
  isLoading?: boolean;
  /** Callback when search changes (for server-side search) */
  onSearchChange?: (search: string) => void;
  /** Controlled search value (for server-side search) */
  searchValue?: string;
  /** Whether an error occurred (shows error message) */
  isError?: boolean;
  /** Custom error message */
  errorMsg?: string;
}

// Default column width when no flex/width/minWidth is specified
const DEFAULT_COL_WIDTH = 150;

/**
 * Compute an initial pixel size for a column based on the available width.
 * flex columns share the remaining space proportionally.
 */
function computeInitialWidths(
  columns: DataTableColumn[],
  containerWidth: number,
  actionsWidth: number,
): Map<string, number> {
  const widths = new Map<string, number>();
  let fixedTotal = actionsWidth;
  let flexTotal = 0;

  for (const col of columns) {
    if (col.width) {
      fixedTotal += col.width;
      widths.set(col.field, col.width);
    } else if (col.flex) {
      flexTotal += col.flex;
    } else {
      const w = col.minWidth || DEFAULT_COL_WIDTH;
      fixedTotal += w;
      widths.set(col.field, w);
    }
  }

  const remaining = Math.max(0, containerWidth - fixedTotal);

  for (const col of columns) {
    if (col.flex && !col.width) {
      const flexWidth =
        flexTotal > 0 ? (col.flex / flexTotal) * remaining : DEFAULT_COL_WIDTH;
      const w = Math.max(
        col.minWidth || DEFAULT_COL_WIDTH,
        Math.floor(flexWidth),
      );
      widths.set(col.field, w);
    }
  }

  return widths;
}

/**
 * Convert our DataTableColumn definitions into TanStack ColumnDefs.
 */
function toTanStackColumns(
  columns: DataTableColumn[],
  initialWidths: Map<string, number>,
): ColumnDef<any>[] {
  return columns.map((col) => ({
    id: col.field,
    accessorFn: (row: any) => {
      if (col.valueGetter) {
        return col.valueGetter(row[col.field], row);
      }
      return row[col.field];
    },
    header: col.headerName ?? col.field,
    cell: (info) => {
      if (col.renderCell) {
        return col.renderCell({
          row: info.row.original,
          value: info.getValue(),
        });
      }
      return info.getValue() ?? '';
    },
    enableSorting: col.sortable !== false,
    enableGlobalFilter: col.filterable !== false,
    enableResizing: !col.width,
    size: initialWidths.get(col.field) || DEFAULT_COL_WIDTH,
    minSize: col.minWidth || 50,
  }));
}

/**
 * DataTable is a responsive table component built on TanStack Table + MUI.
 * On desktop (>=960px) it renders a full table with sorting, filtering,
 * pagination, resizable columns, and a pinned actions column.
 * On mobile (<960px) it renders rows as cards with label/value pairs.
 */
export const DataTable = ({
  columns,
  rows,
  actions,
  defaultAction,
  toolbarActions,
  noDataMsg = 'No data',
  isSearchable = true,
  isPaginated = true,
  isTotalRows = true,
  rowsPerPage = 5,
  paginationMode = 'client',
  rowCount,
  page: controlledPage,
  onPaginationChange,
  isLoading = false,
  onSearchChange,
  searchValue,
  isError = false,
  errorMsg = 'Something went wrong',
}: DataTableProps) => {
  const isServerPagination = paginationMode === 'server';
  const isServerSearch = !!onSearchChange;
  // hooks
  const [windowWidth] = useWindowSize();
  const muiTheme = useTheme();
  const headerColor = alpha(muiTheme.palette.text.primary, 0.7);
  const cellColor = alpha(muiTheme.palette.text.primary, 0.6);
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const isResizingRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // state
  const [isScrolled, setIsScrolled] = useState(false);

  // Track horizontal scroll to show shadow on pinned actions
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    // Scrolled if not at the very end (with 1px tolerance)
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setIsScrolled(!atEnd && el.scrollWidth > el.clientWidth);
  }, []);

  // Check scroll state on mount and when data changes
  useEffect(() => {
    handleScroll();
  });

  // state
  const [internalSearchText, setInternalSearchText] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalPage, setInternalPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowsPerPage);
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Loading when: API is fetching OR user's typed value doesn't match the returned results yet
  const showLoading =
    isLoading || (isServerSearch && internalSearchText !== (searchValue ?? ''));

  // For server-side search, use internal state for immediate display (debounced API call)
  // For client-side search, use internal state directly
  const searchText = internalSearchText;

  // Use controlled page for server-side pagination, internal state otherwise
  const page =
    isServerPagination && controlledPage !== undefined
      ? controlledPage
      : internalPage;
  const setPage = (newPage: number | ((prev: number) => number)) => {
    const resolvedPage =
      typeof newPage === 'function' ? newPage(page) : newPage;
    if (isServerPagination && onPaginationChange) {
      onPaginationChange(resolvedPage, pageSize);
    } else {
      setInternalPage(resolvedPage);
    }
  };

  // derived
  const isMobile = windowWidth < theme.breakpointValues.mdValue;
  const ACTION_BUTTON_WIDTH = 44;
  const ACTION_CELL_PADDING = 32; // 16px each side
  const actionsWidth = actions?.length
    ? actions.length * ACTION_BUTTON_WIDTH + ACTION_CELL_PADDING
    : 0;
  const hasActions = !!actions?.length;

  // Compute initial column widths based on container width
  const containerWidth = containerRef.current?.clientWidth || windowWidth;
  const initialWidths = useMemo(
    () => computeInitialWidths(columns, containerWidth, actionsWidth),
    [columns, containerWidth, actionsWidth],
  );

  // TanStack columns
  const tanStackColumns = useMemo(
    () => toTanStackColumns(columns, initialWidths),
    [columns, initialWidths],
  );

  // TanStack table instance
  const table = useReactTable({
    data: rows,
    columns: tanStackColumns,
    columnResizeMode,
    state: {
      sorting,
      globalFilter: isServerPagination ? undefined : searchText,
      pagination: {
        pageIndex: page,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: isServerPagination ? undefined : getFilteredRowModel(),
    getPaginationRowModel:
      isPaginated && !isServerPagination ? getPaginationRowModel() : undefined,
    manualPagination: isServerPagination,
    pageCount:
      isServerPagination && rowCount
        ? Math.ceil(rowCount / pageSize)
        : undefined,
    globalFilterFn: 'includesString',
    getRowId: (row) => String(row.id),
  });

  // search handler with debounce for server-side search
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (isServerSearch && onSearchChange) {
        // Update local display immediately (shows loading due to mismatch with searchValue)
        setInternalSearchText(value);
        // Debounce the server call
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
          onSearchChange(value);
          if (onPaginationChange) {
            onPaginationChange(0, pageSize);
          }
        }, 500);
      } else {
        setInternalSearchText(value);
        setInternalPage(0);
      }
    },
    [isServerSearch, onSearchChange, onPaginationChange, pageSize],
  );

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // search bar - show for client-side search OR server-side search with onSearchChange
  const showSearch = isSearchable && (!isServerPagination || isServerSearch);
  const searchBar = showSearch ? (
    <TextField
      variant='outlined'
      size='small'
      placeholder='Search...'
      value={searchText}
      onChange={handleSearchChange}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position='start'>
              <Icon icon='magnifying-glass' size='sm' />
            </InputAdornment>
          ),
        },
      }}
      sx={{ minWidth: 240 }}
    />
  ) : null;

  // toolbar
  const toolbar = (showSearch || toolbarActions) && (
    <Stack
      direction='row'
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <Box>{searchBar}</Box>
      {toolbarActions && <Box>{toolbarActions}</Box>}
    </Stack>
  );

  // action buttons renderer
  const renderActions = (row: any) => {
    if (!actions?.length) return null;
    return (
      <Stack
        direction='row'
        spacing={0.5}
        sx={{ alignItems: 'center', justifyContent: 'center' }}
      >
        {actions.map((action, i) => {
          if (action.isHidden?.(row)) return null;
          const disabled = action.isDisabled?.(row) ?? false;
          return (
            <Tooltip
              key={i}
              title={action.label}
              placement='top'
              arrow
              enterDelay={0}
              slotProps={{
                transition: { timeout: 150 },
                popper: {
                  sx: { pointerEvents: 'none' },
                  modifiers: [
                    {
                      name: 'offset',
                      options: { offset: [0, 0] },
                    },
                  ],
                },
              }}
            >
              <span>
                <IconButton
                  size='small'
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onPress(row);
                  }}
                >
                  <Icon icon={action.icon} type={action.iconType} size='sm' />
                </IconButton>
              </span>
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  // mobile card view
  if (isMobile) {
    const filteredRows = table.getFilteredRowModel().rows;
    const displayRows = isPaginated ? table.getRowModel().rows : filteredRows;
    const mobileRowCount =
      isServerPagination && rowCount !== undefined
        ? rowCount
        : filteredRows.length;
    const totalPages = Math.ceil(mobileRowCount / rowsPerPage);

    return (
      <Box>
        {toolbar}

        {isError ? (
          <Typography
            variant='body2'
            color='error'
            sx={{ textAlign: 'center', py: 4 }}
          >
            {errorMsg}
          </Typography>
        ) : showLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              py: 4,
            }}
          >
            {noDataMsg}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {displayRows.map((row) => (
              <Card
                key={row.id}
                elevation={2}
                sx={{
                  cursor: defaultAction ? 'pointer' : 'default',
                }}
                onClick={
                  defaultAction
                    ? () => defaultAction.onPress(row.original)
                    : undefined
                }
              >
                <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                  <Stack spacing={1}>
                    {columns.map((col) => {
                      // Columns with renderCell but no headerName (e.g. avatar)
                      // render the custom cell directly
                      if (!col.headerName && col.renderCell) {
                        return (
                          <Box key={col.field}>
                            {col.renderCell({
                              row: row.original,
                              value: row.original[col.field],
                            })}
                          </Box>
                        );
                      }
                      // Skip columns with no headerName and no renderCell
                      if (!col.headerName) return null;

                      const value = col.renderCell
                        ? col.renderCell({
                            row: row.original,
                            value: col.valueGetter
                              ? col.valueGetter(
                                  row.original[col.field],
                                  row.original,
                                )
                              : row.original[col.field],
                          })
                        : col.valueGetter
                          ? col.valueGetter(
                              row.original[col.field],
                              row.original,
                            )
                          : (row.original[col.field] ?? '—');

                      return (
                        <Stack
                          key={col.field}
                          direction='row'
                          sx={{
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Typography
                            variant='caption'
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 600,
                              minWidth: 100,
                            }}
                          >
                            {col.headerName}
                          </Typography>
                          <Typography
                            variant='body2'
                            component='div'
                            sx={{ textAlign: 'right', flex: 1 }}
                          >
                            {value}
                          </Typography>
                        </Stack>
                      );
                    })}
                    {actions?.length && (
                      <Stack
                        direction='row'
                        spacing={0.5}
                        sx={{ justifyContent: 'flex-end', pt: 0.5 }}
                      >
                        {renderActions(row.original)}
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* Mobile pagination & total */}
        {(isPaginated || isTotalRows) && mobileRowCount > 0 && !showLoading && (
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}
          >
            {isTotalRows && (
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                }}
              >
                {mobileRowCount} row
                {mobileRowCount !== 1 ? 's' : ''}
              </Typography>
            )}
            {isPaginated && totalPages > 1 && (
              <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
                <IconButton
                  size='small'
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <Icon icon='chevron-left' size='sm' />
                </IconButton>
                <Typography variant='caption'>
                  {page + 1} / {totalPages}
                </Typography>
                <IconButton
                  size='small'
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <Icon icon='chevron-right' size='sm' />
                </IconButton>
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    );
  }

  // desktop table view
  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;
  const filteredCount =
    isServerPagination && rowCount !== undefined
      ? rowCount
      : table.getFilteredRowModel().rows.length;

  return (
    <Box ref={containerRef}>
      {toolbar}

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <TableContainer ref={scrollContainerRef} onScroll={handleScroll}>
          <Table
            ref={tableRef}
            sx={{
              width: '100%',
              tableLayout: 'fixed',
            }}
          >
            <TableHead>
              {headerGroups.map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    const canResize = header.column.getCanResize();
                    return (
                      <TableCell
                        key={header.id}
                        onClick={
                          canSort
                            ? (e) => {
                                if (isResizingRef.current) return;
                                header.column.getToggleSortingHandler()?.(e);
                              }
                            : undefined
                        }
                        sx={{
                          width: header.getSize(),
                          position: 'relative',
                          color: headerColor,
                          fontWeight: 'fontWeightMedium',
                          whiteSpace: 'nowrap',
                          py: 1.5,
                          userSelect: 'none',
                          cursor: canSort ? 'pointer' : 'default',
                          ...(!sorted && {
                            '&:hover:not(:has(.DataTable-resizeHandle:hover)) .MuiTableSortLabel-icon':
                              {
                                opacity: '0.5 !important',
                              },
                          }),
                        }}
                        sortDirection={sorted || false}
                      >
                        {canSort ? (
                          <TableSortLabel
                            active={!!sorted}
                            direction={sorted || 'asc'}
                            // Click handled by the TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              pointerEvents: 'none',
                              maxWidth: '100%',
                              '& .MuiTableSortLabel-icon': {
                                opacity: sorted ? 1 : 0,
                                transition: 'opacity 150ms',
                                flexShrink: 0,
                              },
                            }}
                          >
                            <Box
                              component='span'
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </Box>
                          </TableSortLabel>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        )}
                        {/* Resize handle */}
                        {canResize && (
                          <Box
                            className='DataTable-resizeHandle'
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              isResizingRef.current = true;
                              const onMouseUp = () => {
                                // Clear after a tick so the click event
                                // on the header cell sees the flag
                                requestAnimationFrame(() => {
                                  isResizingRef.current = false;
                                });
                                window.removeEventListener(
                                  'mouseup',
                                  onMouseUp,
                                );
                              };
                              window.addEventListener('mouseup', onMouseUp);
                              header.getResizeHandler()(e);
                            }}
                            onTouchStart={header.getResizeHandler()}
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              if (!tableRef.current) return;
                              const colIndex = header.index;
                              const cells = tableRef.current.querySelectorAll(
                                `tr > :nth-child(${colIndex + 1})`,
                              );
                              // Temporarily switch to auto layout so
                              // scrollWidth reflects true content width
                              const prevLayout =
                                tableRef.current.style.tableLayout;
                              tableRef.current.style.tableLayout = 'auto';
                              let maxWidth = 0;
                              cells.forEach((cell) => {
                                const el = cell as HTMLElement;
                                const prev = el.style.width;
                                el.style.width = '1px';
                                maxWidth = Math.max(maxWidth, el.scrollWidth);
                                el.style.width = prev;
                              });
                              tableRef.current.style.tableLayout = prevLayout;
                              if (maxWidth > 0) {
                                table.setColumnSizing((old) => ({
                                  ...old,
                                  [header.column.id]: maxWidth,
                                }));
                              }
                            }}
                            sx={{
                              position: 'absolute',
                              right: -8,
                              top: 0,
                              height: '100%',
                              width: 16,
                              cursor: 'col-resize',
                              zIndex: 1,
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                left: '50%',
                                top: '25%',
                                height: '50%',
                                width: '1px',
                                transform: 'translateX(-50%)',
                                backgroundColor: 'divider',
                                borderRadius: 1,
                                transition: 'background-color 150ms',
                              },
                              '&:hover::after': {
                                backgroundColor: 'primary.main',
                              },
                              ...(header.column.getIsResizing() && {
                                '&::after': {
                                  backgroundColor: 'primary.main',
                                },
                              }),
                            }}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                  {/* Pinned actions header */}
                  {hasActions && (
                    <TableCell
                      sx={{
                        width: actionsWidth,
                        minWidth: actionsWidth,
                        maxWidth: actionsWidth,
                        position: 'sticky',
                        right: 0,
                        backgroundColor: 'background.default',
                        zIndex: 3,
                        textAlign: 'center',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          right: '100%',
                          width: 8,
                          pointerEvents: 'none',
                          background:
                            'linear-gradient(to left, rgba(0,0,0,0.15), transparent)',
                          opacity: isScrolled ? 1 : 0,
                          transition: 'opacity 200ms',
                        },
                      }}
                    />
                  )}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {isError ? (
                <TableRow>
                  <TableCell
                    colSpan={tanStackColumns.length + (actions?.length ? 1 : 0)}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    <Typography variant='body2' color='error'>
                      {errorMsg}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : showLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={tanStackColumns.length + (actions?.length ? 1 : 0)}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : tableRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={tanStackColumns.length + (actions?.length ? 1 : 0)}
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {noDataMsg}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tableRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={
                      defaultAction
                        ? () => defaultAction.onPress(row.original)
                        : undefined
                    }
                    sx={{
                      cursor: defaultAction ? 'pointer' : 'default',
                      // Sticky action cell needs an opaque hover bg to match the row's semi-transparent action.hover overlay on gardenia
                      '&:hover .DataTable-actionsCell': {
                        backgroundColor: darken(
                          muiTheme.palette.background.default,
                          muiTheme.palette.action.hoverOpacity,
                        ),
                      },
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        sx={{
                          width: cell.column.getSize(),
                          color: cellColor,
                          py: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                    {/* Pinned actions cell */}
                    {hasActions && (
                      <TableCell
                        className='DataTable-actionsCell'
                        sx={{
                          width: actionsWidth,
                          minWidth: actionsWidth,
                          maxWidth: actionsWidth,
                          position: 'sticky',
                          right: 0,
                          backgroundColor: 'background.default',
                          zIndex: 2,
                          textAlign: 'center',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            right: '100%',
                            width: 8,
                            pointerEvents: 'none',
                            background:
                              'linear-gradient(to left, rgba(0,0,0,0.15), transparent)',
                            opacity: isScrolled ? 1 : 0,
                            transition: 'opacity 200ms',
                          },
                        }}
                      >
                        {renderActions(row.original)}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {isPaginated && (
          <TablePagination
            component='div'
            count={filteredCount}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onRowsPerPageChange={(e) => {
              const newPageSize = parseInt(e.target.value, 10);
              setPageSize(newPageSize);
              if (isServerPagination && onPaginationChange) {
                onPaginationChange(0, newPageSize);
              } else {
                setInternalPage(0);
              }
            }}
          />
        )}
      </Box>

      {isTotalRows &&
        !showLoading &&
        (isServerPagination && rowCount !== undefined
          ? rowCount
          : rows.length) > 0 && (
          <Typography
            variant='caption'
            sx={{
              color: 'text.secondary',
              mt: 1,
              display: 'block',
            }}
          >
            {isServerPagination && rowCount !== undefined
              ? rowCount
              : rows.length}{' '}
            row
            {(isServerPagination && rowCount !== undefined
              ? rowCount
              : rows.length) !== 1
              ? 's'
              : ''}{' '}
            total
          </Typography>
        )}
    </Box>
  );
};

DataTable.displayName = 'DataTable';

export default DataTable;
